/**
 * TrendChart's x-domain: merges every series' points into one row per x, in
 * axis order. Pure, so the ordering rules are unit-tested apart from recharts.
 *
 *   explicit  `xDomain` given: its order; any x it misses follows, merged as below.
 *   numbers   every x is a number: ascending.
 *   dates     every x is an ISO date string (2026-10-02, 2026-10-02T14:00Z): chronological.
 *   labels    otherwise ("Jul 17"): each new x lands right after the x before it in its own
 *             series (or, leading a series, before the next x it shares), so a series that
 *             skips a point never pushes that point to the end of the axis.
 */
export type TrendX = string | number
export type TrendRow = Record<string, string | number | null>

interface SeriesLike {
  id: string
  points: ReadonlyArray<{ x: TrendX; y: number | null }>
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/

/** Every x in first-seen order, each new one placed next to its neighbours in its own series. */
function mergeByNeighbour(series: ReadonlyArray<SeriesLike>): TrendX[] {
  const order: TrendX[] = []
  const indexOf = (key: string) => order.findIndex((x) => String(x) === key)
  for (const s of series) {
    const keys = s.points.map((p) => String(p.x))
    s.points.forEach((p, i) => {
      if (indexOf(keys[i]) >= 0) return
      let at = order.length
      if (i > 0) at = indexOf(keys[i - 1]) + 1
      else {
        // Leading x of this series: before the first later x of the series that the order already has.
        const next = keys.slice(1).map(indexOf).find((j) => j >= 0)
        if (next !== undefined) at = next
      }
      order.splice(at, 0, p.x)
    })
  }
  return order
}

/** The x values in axis order (see the module comment). */
export function orderTrendX(series: ReadonlyArray<SeriesLike>, xDomain?: ReadonlyArray<TrendX>): TrendX[] {
  const merged = mergeByNeighbour(series)
  if (xDomain?.length) {
    const present = new Set(merged.map(String))
    const listed = new Set(xDomain.map(String))
    return [...xDomain.filter((x) => present.has(String(x))), ...merged.filter((x) => !listed.has(String(x)))]
  }
  if (merged.every((x) => typeof x === 'number')) return [...merged].sort((a, b) => (a as number) - (b as number))
  if (merged.every((x) => typeof x === 'string' && ISO_DATE.test(x) && !Number.isNaN(Date.parse(x)))) {
    return [...merged].sort((a, b) => Date.parse(a as string) - Date.parse(b as string))
  }
  return merged
}

/** One row per x in axis order, with each series' y under its id (absent where it has no point). */
export function mergeTrendRows(series: ReadonlyArray<SeriesLike>, xDomain?: ReadonlyArray<TrendX>): TrendRow[] {
  const rows = new Map<string, TrendRow>(orderTrendX(series, xDomain).map((x) => [String(x), { x }]))
  for (const s of series) {
    for (const p of s.points) {
      const row = rows.get(String(p.x))
      if (row) row[s.id] = p.y
    }
  }
  return [...rows.values()]
}
